import * as React from "react"
import {useState} from "react"
import {LucideIcon} from "lucide-react"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {cn} from "@/lib/utils"
import {useTouchArbitratedTrigger} from "@/main/react/hooks/useTouchArbitratedTrigger"
import {CtaButton} from "./cta/CtaButton"
import {CtaButtonIcon} from "./cta/CtaButtonIcon"
import {CtaSize, CtaVariant} from "./cta/ctaShared"
import { isBrowserOwnedClick } from "@/main/navigation/NavigationPolicy";

/**
 * A dropdown whose items are DESTINATIONS, not actions (wave-19 **Q6**, answer A).
 *
 * <p><b>Why this exists beside `CtaButtonDropdown` rather than inside it.</b> That component is the
 * `…` card-overflow menu: a short list of one-shot actions, none of which is ever "the current one",
 * none of which has a quantity, none of which is an address, and all of which hang off a trigger at a
 * card's top-right. `DiscussionMobileActionBar` was asked to carry the discussion page's NAVIGATION
 * through it, and **every property the action menu lacks surfaced as its own defect in one frame** —
 * no current-item state (`DISCUSSION-51`), no counts (`-55`), no anchors (`-67`), and a hardcoded
 * `align="end"` that only makes sense for a top-right trigger (`-56`). `DISCUSSION-74` is the
 * synthesis row: fixing those one field at a time would grow the action menu with navigation concepts
 * it would then carry into every `…` in the product. So the concepts live here, the overflow menu
 * stays the lean action list it is, and its eight other consumers are untouched.
 *
 * <p>Deliberately a SMALLER surface than the action menu, not a superset: no login gate, no
 * destructive variant, no titled sections, no loading state. Those belong to actions.
 *
 * <p>Design: `designs/01-now/2026-09-07-discussion-mobile-navigation-menu-primitive-design.md`.
 */

/** `DISCUSSION-109`: the open trigger shows the hover wash, keyed on Radix's `data-state`. */
export const NAVIGATION_MENU_TRIGGER_OPEN_CLASS = "data-[state=open]:[--hover-wash-layer:var(--hover-wash)]"

export type NavigationMenuItem = {
    /** Stable id; also the `data-playwright-id` suffix (`dropdown-item-<id>`). */
    id: string
    icon?: LucideIcon | React.ElementType
    text: string
    /**
     * The destination's REAL address, when it has one. Present → the row renders as an `<a href>`
     * through Radix `asChild`, so long-press "Open in new tab" / "Copy link address" work and
     * assistive tech announces a link rather than a menu item (`DISCUSSION-67`, and `DISCUSSION-14`
     * one component over, which is the obligation this restates at a new call site).
     *
     * <p>Absent → a `<button type="button">`. An entry that is a destination on THIS page but has no
     * URL of its own (scroll to the article) is honest as a button and dishonest as an anchor.
     */
    href?: string
    /** `aria-current="page"` plus a non-colour cue. Bound to the caller's own active state. */
    current?: boolean
    /**
     * A trailing, right-aligned, shrinkable slot — a count badge, typically (`DISCUSSION-55`).
     *
     * <p>A SLOT rather than a `count: number` field on purpose: a number here would mean a second copy
     * of the caller's cap and formatting, and two hand-kept copies of a format drift silently. The
     * caller renders its own badge, so there is exactly one formatter.
     */
    trailing?: React.ReactNode
    /** A plain (unmodified) activation. Modified and middle clicks never reach it — the browser owns those. */
    onSelect?: () => void
}

export type NavigationMenuDropdownProps = {
    icon: LucideIcon
    /** Required, not optional: this trigger is icon-only by construction and a bare "button" is `DISCUSSION-70`. */
    ariaLabel: string
    playwrightId?: string
    extraClass?: string
    size?: CtaSize
    variant?: CtaVariant
    items: NavigationMenuItem[]
    /**
     * `DISCUSSION-56`. The action menu hardcodes `side="bottom" align="end"` for a card's top-right
     * trigger; this trigger is the leftmost control in a bar docked to the BOTTOM edge, so Radix
     * flipped the menu upward and then shifted the end-aligned content until it hit the viewport,
     * landing its hairline in the leftmost pixel column — the only surface on the page with no
     * gutter, its border clipped by the display edge and inside the system back-gesture strip.
     *
     * <p>So the anchor is the CALL SITE's to state. The defaults below are the navigation defaults
     * (start-aligned, offset clear of its trigger, never flush to the display edge), not the action
     * menu's.
     */
    side?: "top" | "right" | "bottom" | "left"
    align?: "start" | "center" | "end"
    sideOffset?: number
    collisionPadding?: number
}

// The menu and the discussion page's section strip must agree about which clicks are theirs, which
// is why this reads the shared predicate rather than restating it (`FA-R2`, 2026-09-07).

export function NavigationMenuDropdown({
    icon,
    ariaLabel,
    playwrightId,
    extraClass = "",
    size = "sm",
    variant = "ghost",
    items,
    side = "bottom",
    align = "start",
    sideOffset = 8,
    collisionPadding = 12,
}: NavigationMenuDropdownProps) {
    const [open, setOpen] = useState(false)

    // While the menu is open, close it as soon as the page scrolls: the menu is non-modal, so without
    // this it stays anchored to its trigger and drifts off-screen. Attached on the next frame so the
    // open-time positioning pass cannot self-close it. (Same contract as the action menu.)
    React.useEffect(() => {
        if (!open) {
            return
        }
        let attached = false
        const closeOnScroll = () => setOpen(false)
        const raf = requestAnimationFrame(() => {
            attached = true
            window.addEventListener("scroll", closeOnScroll, {capture: true, passive: true})
        })
        return () => {
            cancelAnimationFrame(raf)
            if (attached) {
                window.removeEventListener("scroll", closeOnScroll, {capture: true} as EventListenerOptions)
            }
        }
    }, [open])

    // ── OPEN ON A TAP, NOT ON TOUCHDOWN ──────────────────────────────────────────────────────────
    //
    // AGENTS.md rule 26, second paragraph, binds this component from its first line: this bar is
    // exactly the surface where the `pointerdown`/`click` two-event contract killed every `…` overflow
    // menu on iOS (2026-09-01) and then on Android (2026-09-02). The whole gesture contract lives in
    // ONE place because the two handlers must agree — decide it from the POINTER SEQUENCE, never from
    // a field on the click (`event.detail` is the click COUNT, derived from the compat mouse events
    // this handler itself suppresses). Every browser lane here is Chromium, so no lane catches it.
    // Read `src/main/react/hooks/useTouchArbitratedTrigger.ts` before changing anything here.
    const triggerGestureProps = useTouchArbitratedTrigger(
        React.useCallback(() => setOpen((previous) => !previous), []),
    )

    if (!items.length) {
        return null
    }

    return (
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
            <DropdownMenuTrigger asChild>
                <CtaButton
                    icon={icon}
                    text={null}
                    ariaLabel={ariaLabel}
                    playwrightId={playwrightId}
                    // `DISCUSSION-109`: while the menu is open the trigger holds the design language's one
                    // hover wash (the same `--hover-wash` a hovered control shows), from Radix's own
                    // `data-state="open"`, so the open state is visible on a device with no hover.
                    extraClass={extraClass + " cta-btn-dropdown " + NAVIGATION_MENU_TRIGGER_OPEN_CLASS}
                    size={size}
                    variant={variant}
                    suppressTooltip={true}
                    requiresLogin={false}
                    {...triggerGestureProps}
                />
            </DropdownMenuTrigger>

            <DropdownMenuPortal>
                <DropdownMenuContent
                    side={side}
                    align={align}
                    sideOffset={sideOffset}
                    collisionPadding={collisionPadding}
                    data-playwright-id={playwrightId ? `${playwrightId}-menu` : undefined}
                    className={cn(
                        // Semantic tokens only (rule 23): the popover surface is `--popover`, its
                        // hairline is `--border`, and a floating layer's elevation is the
                        // `--surface-shadow-lg` TOKEN. `min-w-max` so a count never wraps a label.
                        "min-w-max w-min flex-col gap-1 rounded-md z-layer-top p-2",
                        "bg-popover text-popover-foreground border border-border shadow-[var(--surface-shadow-lg)]",
                    )}
                >
                    <DropdownMenuGroup>
                        {items.map((item) => {
                            const Icon = item.icon

                            // ── THE CLICK CONTRACT ───────────────────────────────────────────────
                            //
                            // A modified / middle click is the reader asking the BROWSER for this URL;
                            // it is left entirely alone (`DISCUSSION-14`). A plain click is the page's,
                            // and goes through the caller's own handler so selection still runs through
                            // its history-backed route state (rule 33 — nothing here writes history).
                            //
                            // Radix's own `onSelect` is SUPPRESSED and the menu closed by hand, in BOTH
                            // branches. Without the suppression a modified click would open the new tab
                            // *and* move the current tab's section, because Radix fires select for it
                            // too. Radix turns Enter/Space on the item into a `click()`, so the keyboard
                            // path is this same path and needs no second handler.
                            const onClick = (event: React.MouseEvent) => {
                                if (!isBrowserOwnedClick(event)) {
                                    event.preventDefault()
                                    item.onSelect?.()
                                }
                                setOpen(false)
                            }

                            const body = (
                                <>
                                    {/* The non-colour half of the current-item cue (`DISCUSSION-51`):
                                        a leading accent bar that is PRESENT or ABSENT, not a hue that
                                        changes. Inactive rows keep a transparent bar of the same width
                                        so nothing shifts as the current item moves. */}
                                    <span
                                        aria-hidden="true"
                                        className={cn(
                                            "h-4 w-0.5 shrink-0 rounded-full",
                                            item.current ? "bg-primary" : "bg-transparent",
                                        )}
                                    />
                                    {Icon && <CtaButtonIcon icon={Icon as LucideIcon} className="stroke-foreground" />}
                                    <span className={cn("min-w-0 flex-1 truncate p-0", item.current && "font-semibold")}>
                                        {item.text}
                                    </span>
                                    {item.trailing != undefined && (
                                        <span className="ml-2 shrink-0">{item.trailing}</span>
                                    )}
                                </>
                            )

                            // `px-3 py-2 text-sm` renders 36px, 8px under the rule-2 floor, so the
                            // coarse-pointer floor is applied here too. Gated on the INPUT DEVICE,
                            // never a breakpoint. NOT `touch-hit-44`: that centres a 44x44 expander,
                            // which on a ~161px-wide menu row would REPLACE a full-width hit area with
                            // a 44px one. A menu row's box may grow.
                            // `DISCUSSION-113`: `text-left`, so a row rendered as a <button> (no href) aligns
                            // its label with the link rows instead of inheriting the button's centring.
                            const rowClass = cn(
                                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground",
                                "touch-manipulation [@media(pointer:coarse)]:min-h-11",
                                "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                            )

                            return (
                                <DropdownMenuItem
                                    key={item.id}
                                    asChild
                                    data-playwright-id={`dropdown-item-${item.id}`}
                                    className="w-full px-0 py-0 standard-hover"
                                    onSelect={(event: Event) => {
                                        try {
                                            event.preventDefault()
                                        } catch {
                                            // ignore if the event shape differs
                                        }
                                    }}
                                >
                                    {item.href ? (
                                        <a
                                            href={item.href}
                                            aria-current={item.current ? "page" : undefined}
                                            className={rowClass}
                                            onClick={onClick}
                                        >
                                            {body}
                                        </a>
                                    ) : (
                                        <button
                                            type="button"
                                            aria-current={item.current ? "page" : undefined}
                                            className={rowClass}
                                            onClick={onClick}
                                        >
                                            {body}
                                        </button>
                                    )}
                                </DropdownMenuItem>
                            )
                        })}
                    </DropdownMenuGroup>
                </DropdownMenuContent>
            </DropdownMenuPortal>
        </DropdownMenu>
    )
}
